import { useState, useEffect, useRef } from 'react';
import { CONSTANTS, DEFAULT_PARAMS } from '../utils/constants';
import { calculateSystemResults, fetchEuroRate } from '../utils/heatPumpCalculations';

const useSystemCalculations = () => {
  const [systems, setSystems] = useState([]);
  
  const [darkMode, setDarkMode] = useState(true); // false yerine true yaparak koyu tema ile başlatıyoruz
  const [solarEnabled, setSolarEnabled] = useState(false);
  const [globalParams, setGlobalParams] = useState({
    ...DEFAULT_PARAMS,
    co2Factor: CONSTANTS.co2Factor
  });
  const [activeAccordion, setActiveAccordion] = useState('equipment');

  // Euro kuru için ref oluşturun (tek seferlik çekme için)
  const euroRateRef = useRef(null);

  const handleEquipmentClick = (equipment) => {
    const nextIndex = systems.length;
    
    // Her ekipman için özel parametreler
    const getInitialParams = (equipmentType) => {
      switch(equipmentType) {
        case 'chiller':
          return {
            capacity: 500,
            efficiency: 75,
            heatingCapacity: 500,
            heatingTemperature: 60,
            coolingCapacity: 500,
            coolingTemperature: 20,
            chillerCop: 6.5,
            workingHours: 5000 // Çalışma saatini ekle
          };
        case 'heatRecovery':
          return {
            capacity: 300,
            efficiency: 80,
            heatingCapacity: 300,
            heatingTemperature: 65,
            coolingCapacity: 250,
            coolingTemperature: 25,
            chillerCop: 5,
            workingHours: 5000 // Çalışma saatini ekle
          };
        case 'coolingTower':
          return {
            capacity: 700,
            efficiency: 70,
            heatingCapacity: 700,
            heatingTemperature: 55,
            coolingCapacity: 600,
            coolingTemperature: 15,
            chillerCop: 4,
            workingHours: 5000 // Çalışma saatini ekle
          };
        default:
          return {
            capacity: 500,
            efficiency: 75,
            heatingCapacity: 500,
            heatingTemperature: 60,
            coolingCapacity: 500,
            coolingTemperature: 20,
            chillerCop: 6,
            workingHours: 5000 // Çalışma saatini ekle
          };
      }
    };
    
    const initialParams = getInitialParams(equipment.id);
    
    const newSystem = {
      id: `system-${nextIndex + 1}`,
      name: `${equipment.name}-${nextIndex + 1}`,
      type: equipment.id,
      ...initialParams
    };

    setSystems([...systems, newSystem]);
  };

  const removeSystem = (idToRemove) => {
    setSystems(prev => prev.filter(system => system.id !== idToRemove));
  };

  const handleGlobalParamChange = (param, value) => {
    setGlobalParams(prev => ({
      ...prev,
      [param]: Number(value)
    }));
  };

  function handleSystemParamChange(id, key, value) {
    setSystems((prev) =>
      prev.map((sys) =>
        sys.id === id
          ? key === null
            ? { ...sys, ...value } // Birden fazla alanı aynı anda güncelle
            : { ...sys, [key]: value }
          : sys
      )
    );
  }

  // Euro kurunu çekmek için
  useEffect(() => {
    const loadEuroRate = async () => {
      if (euroRateRef.current !== null) return; // Zaten değer varsa çıkış yap
    
      try {
        // CORS hatası için fetchEuroRate'i değiştirdik, şimdi sabit değer dönecek
        const rate = await fetchEuroRate();
        euroRateRef.current = rate;
      
        // Global state'e aktar
        setGlobalParams(prev => ({
          ...prev,
          eurRate: rate
        }));
      } catch (error) {
        // Hata durumunda varsayılan değer kullan - artık hata oluşmayacak
        euroRateRef.current = 38;
        setGlobalParams(prev => ({
          ...prev,
          eurRate: 38
        }));
      }
    };
  
    loadEuroRate();
  }, []); // Boş dependency array - sadece bir kez çalışır

  // Sistemler güncellendiğinde hesaplama yap
  useEffect(() => {
    const calculateResults = () => {
      console.log("useSystemCalculations - calculateResults çağrıldı:", { systems, solarEnabled });
      
      if (!systems.length) return;
      
      // systems state'ine doğrudan müdahale etmeden önce yeni bir dizi oluştur
      const updatedSystems = systems.map(system => {
        // Her zaman yeni sonuçları hesapla
        const results = calculateSystemResults(
          system, 
          { ...globalParams, eurRate: euroRateRef.current || 38 }, 
          solarEnabled // Solar değerini burada geçiriyoruz
        );
        
        // Hesaplama sonuçlarında değişiklik olup olmadığını kontrol et
        if (!system.results || JSON.stringify(system.results) !== JSON.stringify(results)) {
          return { ...system, results };
        }
        
        // Değişiklik yoksa referansı koruyarak React'ın gereksiz render'larını önle
        return system;
      });
      
      // Sistemlerin referanslarını karşılaştır, herhangi biri değişmişse state'i güncelle
      const hasChanged = updatedSystems.some((sys, idx) => sys !== systems[idx]);
      if (hasChanged) {
        console.log("Sistemler güncelleniyor:", { hasChanged, solarEnabled });
        setSystems(updatedSystems);
      }
    };

    // Bu fonksiyonu bağımlılıklarımız değiştiğinde çalıştır
    calculateResults();
  }, [systems, globalParams, solarEnabled]); // systems, globalParams ve solarEnabled değiştiğinde çalışacak

  // Solar değeri değiştiğinde sadece solarEnabled'a odaklanan özel bir effect
  useEffect(() => {
    console.log("🔄 Solar değişti:", solarEnabled);
    
    // Solar değişikliklerinin tutarlı uygulanmasını sağlamak için sistemleri güncelle
    if (systems.length) {
      const updatedSystems = systems.map(system => {
        const results = calculateSystemResults(
          system, 
          { ...globalParams, eurRate: euroRateRef.current || 38 }, 
          solarEnabled
        );
        
        return { ...system, results };
      });
      
      setSystems(updatedSystems);
      console.log("🌞 Solar değişti, sistemler güncellendi:", solarEnabled);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [solarEnabled]); // Sadece solarEnabled değiştiğinde çalışacak

  // Toplam sonuçları hesapla
  const getTotalResults = () => {
    if (systems.length === 0) {
      return { totalCapacity: 0, totalSavings: 0, totalCO2: 0, avgCOP: 0 };
    }
    
    return systems.reduce((total, system) => {
      const results = system.results || { cop: 0, savings: 0, co2: 0 };
      // Kapasite olarak Isıtma Kapasitesini (heatingCapacity) baz alıyoruz
      // Eğer undefined ise 0 kabul ediyoruz
      const currentCapacity = Number(system.heatingCapacity) || Number(system.capacity) || 0;
      
      return {
        totalCapacity: total.totalCapacity + currentCapacity,
        totalSavings: total.totalSavings + (Number(results.savings) || 0),
        totalCO2: total.totalCO2 + (Number(results.co2) || 0),
        avgCOP: total.avgCOP + ((Number(results.cop) || 0) / systems.length)
      };
    }, { totalCapacity: 0, totalSavings: 0, totalCO2: 0, avgCOP: 0 });
  };

  const totalResults = getTotalResults();

  return {
    systems,
    darkMode,
    solarEnabled,
    globalParams,
    activeAccordion,
    totalResults,
    setSystems,
    setDarkMode,
    setSolarEnabled,
    setGlobalParams,
    setActiveAccordion,
    handleEquipmentClick,
    removeSystem,
    handleGlobalParamChange,
    handleSystemParamChange
  };
};

export default useSystemCalculations;